import { useGlobalRegionsQuery } from '@gen2/api/global-region/hooks';
import {
  IResetPasswordForm,
  TResetPasswordPayload,
  TResetPasswordErrorResponse,
} from '@gen2/api/reset-password/api';
import { useResetPasswordMutation } from '@gen2/api/reset-password/hooks';
import { useAuth, useRouter } from '@gen2/hooks';
import { ErrorResponse } from '@gen2/utils/formatMessage';
import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Stack, TextField } from '@mui/material';
import { AxiosResponse } from 'axios';
import { has, mapKeys } from 'lodash';
import {
  KeyboardEventHandler,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import { PasswordHintList } from '../components/hint-list/hint-list';
import AuthLayout from '../layout/AuthLayout/AuthLayout';
import { Title, StyledSubmit } from './reset-password.styled';

const schema = Yup.object().shape({
  password: Yup.string()
    .required('password.required')
    .matches(
      /^(?=.*[!@#$%^&*'])(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{15,99}$/,
      'password.pattern',
    ),
  password_confirmation: Yup.string()
    .required('confirmPassword.required')
    .oneOf([Yup.ref('password'), ''], 'confirmPassword.match'),
});

const initialFormValues: IResetPasswordForm = {
  password: '',
  password_confirmation: '',
};

const ResetPassword = () => {
  const { t } = useTranslation('validation');
  const [searchParams] = useSearchParams();
  const router = useRouter();
  const { user } = useAuth();

  const [isShow, setIsShow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState<ReactNode[] | string[]>([]);
  const [initialized, setInitialized] = useState<boolean>(false);
  const { data } = useGlobalRegionsQuery();
  const { mutate } = useResetPasswordMutation();

  const regions = useMemo(() => {
    return data ?? [];
  }, [data]);

  useMemo(() => {
    const region = regions.find((r) => r.name === searchParams.get('region'));
    if (region) {
      localStorage.setItem('regionEndpoint', region.endpoint);
      setInitialized(true);
    }
  }, [regions, searchParams]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<IResetPasswordForm>({
    defaultValues: initialFormValues,
    resolver: yupResolver(schema),
  });

  //Navigate to dashboard if the user logged in.
  useEffect(() => {
    if (user) {
      router.navigate('/dashboard');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  //Navigate to Forgot Password if the required parameters are missing.
  useEffect(() => {
    const email = searchParams.get('email');
    const token = searchParams.get('token');
    const signature = searchParams.get('signature');
    const expires = searchParams.get('expires');
    const region = searchParams.get('region');

    if (
      email === null ||
      email.trim() === '' ||
      token === null ||
      token.trim() === '' ||
      signature === null ||
      signature.trim() === '' ||
      expires === null ||
      expires.trim() === '' ||
      region === null ||
      region.trim() === ''
    ) {
      router.navigate('/forgot-password');
    }

    const payload: TResetPasswordPayload = {
      data: initialFormValues,
      params: { email, expires, token, signature },
    };

    if (initialized) {
      mutate(payload, {
        onError: (error: unknown) => {
          const axiosError = error as AxiosResponse;
          if (axiosError) {
            const { status } = axiosError;
            if (status === 403) {
              router.navigate('/forgot-password?invalid=1');
            }
          }
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, searchParams]);

  const password = watch('password');

  const onSubmit: SubmitHandler<IResetPasswordForm> = (form) => {
    setIsLoading(true);

    const payload: TResetPasswordPayload = {
      data: form,
      params: {
        email: searchParams.get('email'),
        expires: searchParams.get('expires'),
        token: searchParams.get('token'),
        signature: searchParams.get('signature'),
      },
    };

    mutate(payload, {
      onError: (error: unknown) => {
        const axiosError = error as AxiosResponse;
        if (axiosError) {
          const { status, data } = axiosError;
          setIsShow(true);
          if (status === 403) {
            router.navigate('/forgot-password?invalid=1');
          }
          if (status === 422) {
            const { errors }: TResetPasswordErrorResponse =
              data as ErrorResponse;

            if (
              errors &&
              (has(errors, 'password') || has(errors, 'password_confirmation'))
            ) {
              let errorMessages: string[] = [];

              mapKeys(errors ?? [], (value) => {
                if (value && Array.isArray(value)) {
                  errorMessages = [...errorMessages, ...value];
                }
              });

              setMessages(errorMessages);
            }
          }
          if (status === 429) {
            setMessages([data.message]);
          }
          if (status >= 500) {
            setMessages([t('error.500', { ns: 'common' })]);
          }
        }
      },
      onSuccess: () => {
        router.navigate('/login?reset_success=1');
      },
      onSettled: () => {
        setIsLoading(false);
      },
    });
  };

  const handleKeyPress: KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === ' ') {
      event.preventDefault();
    }
  };

  return (
    <AuthLayout isShowAlert={isShow}>
      <header>
        <Title data-cy="title">
          {t('reset_password.title', { ns: 'page' })}
        </Title>
      </header>
      <form noValidate>
        <Stack spacing={3} sx={{ flexWrap: 'wrap' }}>
          {isShow && messages.length > 0 && (
            <Alert data-cy="alert" icon={false} severity="error" color="error">
              {messages &&
                messages.map((message, idx) => {
                  return <div key={idx}>{message}</div>;
                })}
            </Alert>
          )}

          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                id="password"
                type="password"
                label={t('password.label')}
                fullWidth
                required
                onKeyDown={handleKeyPress}
                error={Boolean(errors.password)}
                helperText={
                  errors.password?.message && t(errors.password.message)
                }
                inputProps={{
                  'data-cy': 'password-input',
                }}
              />
            )}
          />
          <Controller
            name="password_confirmation"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                id="confirmPassword"
                type="password"
                fullWidth
                required
                onKeyDown={handleKeyPress}
                label={t('confirmPassword.label')}
                error={Boolean(errors.password_confirmation)}
                helperText={
                  errors.password_confirmation?.message &&
                  t(errors.password_confirmation.message)
                }
                inputProps={{
                  'data-cy': 'confirm-password-input',
                }}
              />
            )}
          />

          {password && <PasswordHintList password={password} />}

          <StyledSubmit
            variant="contained"
            color="primary"
            loading={isLoading}
            type="submit"
            onClick={handleSubmit(onSubmit)}
            data-cy="submit"
          >
            {t('reset_password.btn', { ns: 'page' })}
          </StyledSubmit>
        </Stack>
      </form>
    </AuthLayout>
  );
};

export default ResetPassword;
